// src/Context/AuthGateContext.jsx
// Gate por acción (soft wall): cualquier componente puede pedir auth con
// requireAuth(cb). Si hay sesión, corre cb; si no, abre el AuthModal y al
// autenticarse retoma la acción pendiente. Monta UN solo AuthModal global.
import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import AuthModal from '../components/AuthModal/AuthModal';

const AuthGateContext = createContext(null);

export function AuthGateProvider({ children }) {
  const { user }        = useAuth();
  const [open, setOpen] = useState(false);
  const [tab, setTab]   = useState('login');
  const pendingRef      = useRef(null);

  // Corre cb si hay sesión; si no, abre el modal y guarda la acción para retomarla.
  const requireAuth = useCallback((cb, opts = {}) => {
    if (user) { cb && cb(); return true; }
    pendingRef.current = cb || null;
    setTab(opts.tab || 'login');
    setOpen(true);
    return false;
  }, [user]);

  // Abre el modal sin acción pendiente (botones "Entrar"/"Registrarse").
  const openAuth = useCallback((tab = 'login') => {
    pendingRef.current = null;
    setTab(tab);
    setOpen(true);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    pendingRef.current = null;
  }, []);

  const handleSuccess = useCallback(() => {
    setOpen(false);
    const cb = pendingRef.current;
    pendingRef.current = null;
    if (cb) setTimeout(cb, 0); // retoma la acción después de cerrar el modal
  }, []);

  return (
    <AuthGateContext.Provider value={{ requireAuth, openAuth, isAuthenticated: !!user }}>
      {children}
      <AuthModal open={open} initialTab={tab} onClose={close} onSuccess={handleSuccess} />
    </AuthGateContext.Provider>
  );
}

export function useAuthGate() {
  return useContext(AuthGateContext) || {
    // Fallback seguro si no hay provider: no rompe (corre la acción directo).
    requireAuth: (cb) => { cb && cb(); return true; },
    openAuth: () => {},
    isAuthenticated: false,
  };
}
